import { createEvents } from 'ics'

/**
 * Convert session data to ICS calendar format and trigger download
 * @param {Array} sessions - Array of session objects to export
 * @param {string} filename - Optional filename for the download
 */
export async function exportToCalendar(sessions, filename = 'editions-vibes-schedule.ics') {
  try {
    // Convert sessions to ICS event format
    const events = sessions.map(session => {
      const startDate = new Date(session.startTime)
      const endDate = new Date(session.endTime)
      
      // Create speaker list for description
      const speakerList = session.speakers && session.speakers.length > 0
        ? `\n\nSpeakers: ${session.speakers.map(s => `${s.name} (${s.title})`).join(', ')}`
        : ''
      
      // Build description with session details
      const description = `${session.description || 'Shopify Editions .DEV session'}${speakerList}\n\nCategory: ${session.category}\n\nGenerated by Editions Vibes - ufe.ai/e`
      
      return {
        title: session.title,
        description: description,
        start: [
          startDate.getFullYear(),
          startDate.getMonth() + 1, // ICS months are 1-indexed
          startDate.getDate(),
          startDate.getHours(),
          startDate.getMinutes()
        ],
        end: [
          endDate.getFullYear(),
          endDate.getMonth() + 1,
          endDate.getDate(),
          endDate.getHours(),
          endDate.getMinutes()
        ],
        location: session.location || 'Shopify Editions .DEV - Toronto',
        status: 'CONFIRMED',
        busyStatus: 'BUSY',
        url: 'https://editions.shopify.com',
        categories: [session.category, 'Shopify Editions'],
        organizer: { name: 'Shopify Editions', email: 'editions@shopify.com' }
      }
    })

    // Generate ICS file content
    const { error, value } = createEvents(events)
    
    if (error) {
      console.error('Error creating calendar events:', error)
      throw new Error('Failed to create calendar file')
    }

    // Trigger download
    const blob = new Blob([value], { type: 'text/calendar;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    link.style.display = 'none'
    
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    
    // Clean up the URL object
    URL.revokeObjectURL(url)
    
    return { success: true, count: sessions.length }
    
  } catch (error) {
    console.error('Calendar export failed:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Export a single session to calendar
 * @param {Object} session - Session object to export
 * @returns {Promise<Object>} - Result object with success status and message
 */
export async function exportSessionToCalendar(session) {
  try {
    const filename = `${session.title.replace(/\s+/g, '-').toLowerCase()}.ics`
    const result = await exportToCalendar([session], filename)
    
    if (result.success) {
      return {
        success: true,
        message: `Successfully exported "${session.title}" to your calendar!`
      }
    } else {
      return {
        success: false,
        message: `Export failed: ${result.error}`
      }
    }
  } catch (error) {
    return {
      success: false,
      message: `Export failed: ${error.message}`
    }
  }
}

/**
 * Format session count for user feedback
 * @param {number} count - Number of sessions exported
 * @returns {string} - User-friendly message
 */
export function getExportMessage(count) {
  if (count === 0) {
    return 'No sessions to export. Star some sessions first!'
  }
  
  if (count === 1) {
    return 'Successfully exported 1 session to your calendar!'
  }
  
  return `Successfully exported ${count} sessions to your calendar!`
} 